import { useSyncExternalStore } from 'react';

const STORAGE_KEY = 'sshop-compare-products';
const CHANGE_EVENT = 'sshop:comparison-changed';
export const MAX_COMPARE_PRODUCTS = 3;

export interface ComparedProduct {
  slug: string;
  categorySlug: string;
}

function snapshot(): string {
  try {
    return localStorage.getItem(STORAGE_KEY) ?? '[]';
  } catch {
    return '[]';
  }
}

function subscribe(callback: () => void): () => void {
  window.addEventListener(CHANGE_EVENT, callback);
  window.addEventListener('storage', callback);
  return () => {
    window.removeEventListener(CHANGE_EVENT, callback);
    window.removeEventListener('storage', callback);
  };
}

function read(): ComparedProduct[] {
  try {
    const data: unknown = JSON.parse(snapshot());
    if (!Array.isArray(data)) return [];
    return data
      .filter(
        (item): item is ComparedProduct =>
          typeof item === 'object' &&
          item !== null &&
          typeof item.slug === 'string' &&
          typeof item.categorySlug === 'string',
      )
      .slice(0, MAX_COMPARE_PRODUCTS);
  } catch {
    return [];
  }
}

function write(items: ComparedProduct[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    /* storage may be unavailable */
  }
  window.dispatchEvent(new Event(CHANGE_EVENT));
}

export function useComparison() {
  const raw = useSyncExternalStore(subscribe, snapshot, () => '[]');
  let items: ComparedProduct[];
  try {
    const data: unknown = JSON.parse(raw);
    items = Array.isArray(data)
      ? data
          .filter(
            (item): item is ComparedProduct =>
              typeof item === 'object' &&
              item !== null &&
              typeof item.slug === 'string' &&
              typeof item.categorySlug === 'string',
          )
          .slice(0, MAX_COMPARE_PRODUCTS)
      : [];
  } catch {
    items = [];
  }

  const toggle = (
    product: ComparedProduct,
  ): 'added' | 'removed' | 'different-category' | 'full' => {
    const current = read();
    if (current.some((item) => item.slug === product.slug)) {
      write(current.filter((item) => item.slug !== product.slug));
      return 'removed';
    }
    if (current.length > 0 && current[0]?.categorySlug !== product.categorySlug)
      return 'different-category';
    if (current.length >= MAX_COMPARE_PRODUCTS) return 'full';
    write([...current, product]);
    return 'added';
  };

  return {
    items,
    toggle,
    remove: (slug: string) => write(read().filter((item) => item.slug !== slug)),
    clear: () => write([]),
  };
}
